/**
 * SKI AXIS · /axis-circuit — The Axis Circuit public framework page.
 *
 * Single-page editorial introduction to The Axis Circuit — the global
 * tournament that runs on top of The Axis Index methodology.
 *
 * Format: 11 Chapter Qualifiers → 3 Regional Salons → Grand Finale (Dubai)
 * Cadence: Demo Day (Nov 2026) · Salons (Q1/Q2/Q3 2027) · Grand Finale (Q4 2027)
 *
 * CTAs (4 inquiry intents):
 *   - sponsor (Title / Category / Stage / Media)
 *   - partner (universities, funds, industry co-hosts)
 *   - jury (jury or methodology committee nominee)
 *   - founder (Cohort 2 onwards founder applicant)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Trophy, Globe2, Layers, Coins, Handshake, Gavel, GraduationCap,
  CheckCircle2, AlertCircle, Loader2, Crown, Megaphone, Cpu, ScrollText,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.cir-root{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;}
.cir-shell{max-width:1180px;margin:0 auto;padding:64px 32px;}
@media(max-width:680px){.cir-shell{padding:40px 22px;}}

/* HERO */
.cir-hero{padding:64px 0 48px;border-bottom:1px solid rgba(13,27,42,.08);}
.cir-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:22px;}
.cir-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.cir-h1{font-family:'Cormorant Display',serif;font-size:clamp(46px,6.5vw,82px);font-weight:700;line-height:.98;letter-spacing:-.025em;margin:0 0 22px;color:#0D1B2A;max-width:1100px;}
.cir-h1 em{font-style:italic;color:#C8A84B;font-weight:600;}
.cir-h1 sup{font-family:'DM Sans',sans-serif;font-size:24px;color:#C8A84B;font-weight:700;letter-spacing:0;vertical-align:super;}
.cir-sub{font-size:19px;line-height:1.5;color:#3D4A5C;max-width:820px;font-weight:300;margin:0 0 32px;}
.cir-meta-row{display:flex;flex-wrap:wrap;gap:18px;margin-bottom:30px;font-family:'Cormorant Display',serif;font-size:14.5px;color:#3D4A5C;font-style:italic;}
.cir-meta-row span{color:#0D1B2A;font-weight:600;}
.cir-meta-row em{color:#C8A84B;}
.cir-cta-row{display:flex;flex-wrap:wrap;gap:14px;margin-top:8px;}
.cir-cta{display:inline-flex;align-items:center;gap:10px;background:#0D1B2A;color:#C8A84B;padding:18px 32px;font-size:12px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;border:1px solid #0D1B2A;cursor:pointer;font-family:inherit;transition:all .2s;}
.cir-cta:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.cir-cta-2{display:inline-flex;align-items:center;gap:10px;color:#0D1B2A;padding:18px 28px;font-size:12px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;text-decoration:none;border:1px solid rgba(13,27,42,.25);cursor:pointer;font-family:inherit;background:transparent;transition:all .2s;}
.cir-cta-2:hover{border-color:#C8A84B;color:#C8A84B;}

/* MANIFESTO */
.cir-manifesto{margin-top:80px;background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;padding:64px 56px;border-left:4px solid #C8A84B;position:relative;overflow:hidden;}
@media(max-width:680px){.cir-manifesto{padding:42px 28px;}}
.cir-manifesto::before{content:'';position:absolute;top:-80px;right:-80px;width:300px;height:300px;background:radial-gradient(circle,rgba(200,168,75,.14) 0%,transparent 70%);}
.cir-manifesto-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;margin-bottom:14px;display:flex;align-items:center;gap:14px;}
.cir-manifesto-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.cir-manifesto-h{font-family:'Cormorant Display',serif;font-size:clamp(30px,4vw,48px);font-weight:700;line-height:1.1;letter-spacing:-.014em;margin:0 0 20px;color:#FAF7F0;}
.cir-manifesto-h em{font-style:italic;color:#C8A84B;}
.cir-manifesto-p{font-size:16px;line-height:1.75;color:rgba(250,247,240,.82);font-weight:300;margin:0 0 14px;max-width:880px;}
.cir-manifesto-p strong{color:#C8A84B;font-weight:700;}

/* SECTION HEADER */
.cir-h2{font-family:'Cormorant Display',serif;font-size:clamp(36px,4.5vw,54px);font-weight:700;line-height:1.04;letter-spacing:-.018em;margin:0 0 14px;color:#0D1B2A;}
.cir-h2 em{font-style:italic;color:#C8A84B;}
.cir-h2-sub{font-size:15.5px;color:#3D4A5C;max-width:820px;margin:0 0 36px;line-height:1.55;}

/* TOURNAMENT FORMAT */
.cir-format{margin-top:96px;}
.cir-format-rail{display:grid;grid-template-columns:1.4fr 1fr 1fr;gap:14px;margin-top:28px;}
@media(max-width:880px){.cir-format-rail{grid-template-columns:1fr;}}
.cir-stage{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:30px 28px;display:flex;flex-direction:column;gap:10px;}
.cir-stage-num{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:14px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;}
.cir-stage-h{font-family:'Cormorant Display',serif;font-size:26px;font-weight:700;color:#0D1B2A;line-height:1.1;letter-spacing:-.012em;margin:0;}
.cir-stage-h em{font-style:italic;color:#C8A84B;}
.cir-stage-when{font-size:11px;letter-spacing:.22em;font-weight:800;text-transform:uppercase;color:#8A6A14;background:#FBF8EE;display:inline-block;padding:6px 12px;align-self:flex-start;}
.cir-stage-d{font-size:13.5px;color:#3D4A5C;line-height:1.65;margin:6px 0 0;flex:1;}
.cir-cities{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;}
.cir-city{font-size:10.5px;letter-spacing:.18em;font-weight:700;color:#0D1B2A;background:#FBF8EE;padding:5px 10px;border:1px solid #E8DFC9;text-transform:uppercase;}

/* PRIZE ARCHITECTURE */
.cir-prizes{margin-top:96px;}
.cir-prize-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-top:28px;}
@media(max-width:980px){.cir-prize-grid{grid-template-columns:1fr 1fr;}}
@media(max-width:560px){.cir-prize-grid{grid-template-columns:1fr;}}
.cir-prize{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:28px 24px;display:flex;flex-direction:column;gap:10px;}
.cir-prize.feat{background:linear-gradient(180deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;border:1px solid #C8A84B;border-left:4px solid #C8A84B;}
.cir-prize-icn{width:42px;height:42px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:rgba(200,168,75,.12);color:#C8A84B;}
.cir-prize.feat .cir-prize-icn{background:rgba(200,168,75,.18);}
.cir-prize-eb{font-size:10.5px;letter-spacing:.28em;font-weight:800;color:#C8A84B;text-transform:uppercase;}
.cir-prize-name{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:#0D1B2A;line-height:1.15;letter-spacing:-.005em;margin:0 0 4px;}
.cir-prize.feat .cir-prize-name{color:#FAF7F0;}
.cir-prize.feat .cir-prize-name em{color:#C8A84B;font-style:italic;}
.cir-prize-amt{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:32px;font-weight:700;line-height:1;letter-spacing:-.018em;margin:4px 0;}
.cir-prize-amt sup{font-size:14px;letter-spacing:0;font-weight:600;}
.cir-prize-d{font-size:13px;color:#3D4A5C;line-height:1.6;margin:6px 0 0;}
.cir-prize.feat .cir-prize-d{color:rgba(250,247,240,.82);}
.cir-prize-extra{margin-top:auto;font-size:12px;color:#0D1B2A;font-weight:600;border-top:1px solid #FAF0DC;padding-top:10px;}
.cir-prize.feat .cir-prize-extra{border-top-color:rgba(200,168,75,.2);color:#C8A84B;}

/* SPONSOR TIERS */
.cir-sponsors{margin-top:96px;}
.cir-spons-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:28px;}
@media(max-width:980px){.cir-spons-grid{grid-template-columns:repeat(2,1fr);}}
@media(max-width:560px){.cir-spons-grid{grid-template-columns:1fr;}}
.cir-spon{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:28px 22px;display:flex;flex-direction:column;gap:10px;}
.cir-spon-tier{font-size:10.5px;letter-spacing:.28em;color:#C8A84B;font-weight:800;text-transform:uppercase;}
.cir-spon-name{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#0D1B2A;line-height:1.15;letter-spacing:-.012em;margin:0;}
.cir-spon-price{font-family:'Cormorant Display',serif;font-size:30px;font-weight:700;font-style:italic;color:#C8A84B;line-height:1;letter-spacing:-.018em;margin:6px 0;}
.cir-spon-price sup{font-size:13px;letter-spacing:0;}
.cir-spon ul{margin:6px 0 0;padding:0;list-style:none;}
.cir-spon li{font-size:12.5px;color:#3D4A5C;line-height:1.55;padding:6px 0 6px 14px;border-top:1px solid #FAF0DC;position:relative;}
.cir-spon li:first-child{border-top:0;}
.cir-spon li::before{content:'·';color:#C8A84B;position:absolute;left:2px;top:5px;font-weight:800;}

/* ROADMAP */
.cir-roadmap{margin-top:96px;}
.cir-rm-rail{margin-top:32px;display:grid;grid-template-columns:repeat(5,1fr);gap:8px;}
@media(max-width:980px){.cir-rm-rail{grid-template-columns:repeat(2,1fr);}}
@media(max-width:560px){.cir-rm-rail{grid-template-columns:1fr;}}
.cir-rm{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:24px 18px;display:flex;flex-direction:column;gap:6px;}
.cir-rm-yr{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:30px;font-weight:700;line-height:1;letter-spacing:-.02em;}
.cir-rm-name{font-family:'Cormorant Display',serif;font-size:18px;font-weight:700;color:#0D1B2A;line-height:1.2;letter-spacing:-.005em;margin:2px 0 4px;}
.cir-rm-d{font-size:12px;color:#3D4A5C;line-height:1.55;margin:0;}

/* FOUR CTAS */
.cir-engage{margin-top:96px;background:#FBF8EE;border:1px solid #E8DFC9;border-left:4px solid #C8A84B;padding:42px 48px;}
@media(max-width:680px){.cir-engage{padding:30px 26px;}}
.cir-engage-eb{font-size:11px;letter-spacing:.32em;font-weight:700;color:#8A6A14;text-transform:uppercase;margin-bottom:10px;}
.cir-engage-h{font-family:'Cormorant Display',serif;font-size:clamp(28px,4vw,40px);font-weight:700;color:#0D1B2A;line-height:1.1;letter-spacing:-.014em;margin:0 0 14px;}
.cir-engage-h em{font-style:italic;color:#C8A84B;}
.cir-engage-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:24px;}
@media(max-width:980px){.cir-engage-grid{grid-template-columns:repeat(2,1fr);}}
@media(max-width:560px){.cir-engage-grid{grid-template-columns:1fr;}}
.cir-engage-card{background:#fff;border:1px solid #E8DFC9;padding:22px 20px;display:flex;flex-direction:column;gap:8px;text-align:left;cursor:pointer;font-family:inherit;transition:all .2s;}
.cir-engage-card:hover{border-color:#C8A84B;transform:translateY(-3px);box-shadow:0 12px 24px rgba(13,27,42,.06);}
.cir-engage-card-icn{width:38px;height:38px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:rgba(200,168,75,.14);color:#C8A84B;}
.cir-engage-card-h{font-family:'Cormorant Display',serif;font-size:20px;font-weight:700;color:#0D1B2A;margin:4px 0 2px;line-height:1.2;letter-spacing:-.005em;}
.cir-engage-card-d{font-size:12.5px;color:#3D4A5C;line-height:1.55;margin:0;flex:1;}
.cir-engage-card-cta{display:inline-flex;align-items:center;gap:6px;font-size:11px;letter-spacing:.18em;font-weight:800;color:#0D1B2A;text-transform:uppercase;margin-top:6px;}
.cir-engage-card:hover .cir-engage-card-cta{color:#C8A84B;}

/* INDEX TIE-IN */
.cir-tieIn{margin-top:80px;padding:32px 40px;background:#fff;border:1px solid #E8DFC9;border-left:3px solid #C8A84B;display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:24px;}
@media(max-width:680px){.cir-tieIn{padding:24px 22px;}}
.cir-tieIn-l{flex:1;min-width:280px;}
.cir-tieIn-eb{font-size:10.5px;letter-spacing:.26em;text-transform:uppercase;color:#8A6A14;font-weight:700;margin-bottom:8px;}
.cir-tieIn-d{font-size:14.5px;line-height:1.6;color:#0D1B2A;margin:0;font-style:italic;font-family:'Cormorant Display',serif;font-size:17px;}
.cir-tieIn-d strong{color:#C8A84B;font-weight:700;font-style:normal;font-family:'DM Sans',sans-serif;letter-spacing:.06em;}

/* MODAL */
.cir-modal-bg{position:fixed;inset:0;background:rgba(13,27,42,.6);z-index:50;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(4px);}
.cir-modal{background:#FAF7F0;max-width:640px;width:100%;max-height:90vh;overflow-y:auto;border:1px solid #C8A84B;}
.cir-modal-hd{background:#0D1B2A;color:#FAF7F0;padding:24px 32px;border-bottom:3px solid #C8A84B;display:flex;align-items:center;justify-content:space-between;gap:12px;}
.cir-modal-eb{font-size:10px;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;font-weight:800;margin-bottom:4px;}
.cir-modal-h{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#FAF7F0;margin:0;line-height:1.2;}
.cir-modal-h em{font-style:italic;color:#C8A84B;}
.cir-modal-x{background:transparent;color:rgba(250,247,240,.6);border:1px solid rgba(250,247,240,.18);width:34px;height:34px;cursor:pointer;font-size:18px;line-height:1;flex-shrink:0;}
.cir-modal-x:hover{color:#C8A84B;border-color:#C8A84B;}
.cir-modal-bd{padding:30px 32px;}
.cir-fld{display:flex;flex-direction:column;gap:6px;margin-bottom:16px;}
.cir-lbl{font-size:11px;font-weight:700;letter-spacing:.2em;color:#3D4A5C;text-transform:uppercase;}
.cir-lbl em{color:#C8A84B;font-style:normal;font-weight:800;}
.cir-input,.cir-area{font-family:inherit;font-size:14px;color:#0D1B2A;background:#fff;border:1px solid #E8DFC9;padding:13px 14px;outline:none;transition:border-color .15s;}
.cir-input:focus,.cir-area:focus{border-color:#C8A84B;box-shadow:0 0 0 3px rgba(200,168,75,.08);}
.cir-area{min-height:90px;resize:vertical;line-height:1.55;}
.cir-submit{margin-top:8px;width:100%;background:#C8A84B;color:#0D1B2A;border:none;padding:16px 28px;font-family:inherit;font-size:12px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;transition:all .2s;}
.cir-submit:hover:not(:disabled){background:#0D1B2A;color:#C8A84B;}
.cir-submit:disabled{opacity:.55;cursor:not-allowed;}
.cir-err{background:rgba(173,98,64,.08);border-left:3px solid #ad6240;color:#ad6240;padding:12px 16px;margin-bottom:14px;font-size:13px;display:flex;align-items:center;gap:10px;}
.cir-success{padding:34px 32px;text-align:center;}
.cir-success-icn{display:inline-flex;width:54px;height:54px;border-radius:50%;background:rgba(200,168,75,.18);border:2px solid #C8A84B;color:#C8A84B;align-items:center;justify-content:center;margin-bottom:12px;}
.cir-success-h{font-family:'Cormorant Display',serif;font-size:28px;font-weight:700;color:#0D1B2A;line-height:1.05;letter-spacing:-.012em;margin:0 0 12px;}
.cir-success-h em{font-style:italic;color:#C8A84B;}
.cir-success-p{font-size:14px;color:#3D4A5C;line-height:1.65;max-width:480px;margin:0 auto;}
`;

const CIRCUIT_CITIES = [
  'Dubai', 'London', 'Amsterdam', 'Mumbai', 'Bangalore',
  'Riyadh', 'Doha', 'Singapore', 'Kuala Lumpur', 'Nairobi', 'Lagos',
];

const STAGES = [
  {
    num: 'Stage 01 · Local',
    h: '11 <em>Chapter Qualifiers</em>',
    when: 'Quarterly · Per Cohort',
    d: 'Each Chapter Lead nominates ≤ 5 founders. Local jury + Chapter Lead score against the Axis Index v1.2 rubric (30%) and live pitch (70%). One Chapter Representative + one runner-up advance per corridor.',
    cities: CIRCUIT_CITIES,
  },
  {
    num: 'Stage 02 · Regional',
    h: '3 <em>Regional Salons</em>',
    when: 'APAC · GCC · EMEA',
    d: 'Mid-size convening · 150 attendees per Salon · LinkedIn Live · one academic + one fund partner per Salon · 1 Regional Champion + cross-region wildcards advance to the Grand Finale.',
  },
  {
    num: 'Stage 03 · Global',
    h: 'The <em>Grand Finale</em>',
    when: 'Q4 · Paired with Invest UAE Forum',
    d: '8 finalists · 2-day Dubai institutional spectacle · USD $100K+ cash pool · committed term sheets · Hall of Fame induction · audience LinkedIn-Live live polling + closed-jury verdict.',
  },
];

const PRIZES = [
  {
    icn: <Trophy className="h-5 w-5" />, eb: '01 · Headline',
    name: 'The Axis Prize · Founder of the Cohort',
    amt: 'USD $50,000',
    d: 'Cash + 12 months of Sovereign Capital introductions through SKI Axis\'s family-office, free-zone, and institutional investor network.',
    extra: 'Featured · Top tier · Global headline',
    feat: true,
  },
  {
    icn: <Coins className="h-5 w-5" />, eb: '02 · Capital',
    name: "Investor's Pick · Committed Term Sheet",
    amt: 'Term Sheet',
    d: 'A pre-vetted SAFE or convertible from one of the Title Sovereign Partners — investment, not just cash. The structural differentiator no other competition offers.',
    extra: 'Up to USD $250,000 · Title Sovereign Partner',
  },
  {
    icn: <Layers className="h-5 w-5" />, eb: '03 · Methodology',
    name: 'Best Governance Architecture',
    amt: 'USD $15,000',
    d: 'For the founder with the highest Axis Index Bronze score. Cash + a featured case study on the SKI Axis platform + 12 months Investors Circle membership.',
    extra: 'Anchored by The Axis Index™',
  },
  {
    icn: <Cpu className="h-5 w-5" />, eb: '04 · Modern',
    name: 'Best AI / Decision Infrastructure',
    amt: 'USD $15,000',
    d: 'For the founder demonstrating the strongest AI-native operating posture — Dimension 3 of the Index. Cash + 12-month Mantle OS subscription.',
    extra: 'Mantle OS partnership',
  },
  {
    icn: <Globe2 className="h-5 w-5" />, eb: '05 · Cross-Border',
    name: 'Best Cross-Border Capital Story',
    amt: 'USD $15,000',
    d: 'For the founder operating cleanly across two or more jurisdictions. Cash + five pre-vetted family-office introductions across the SKI Axis Chapter network.',
    extra: 'Chapter Lead network introductions',
  },
  {
    icn: <Megaphone className="h-5 w-5" />, eb: '06 · Audience',
    name: 'Audience Choice · LinkedIn Live Vote',
    amt: 'USD $10,000',
    d: 'Voted by the live LinkedIn audience during the Grand Finale broadcast. Cash + a confirmed speaker slot at the next Invest UAE Forum.',
    extra: 'Forum stage · public-facing',
  },
];

const SPONSOR_TIERS = [
  {
    tier: '🏆 Title Sovereign Partner',
    name: 'Title',
    price: 'USD $250K+',
    items: [
      'Naming rights · "The [Sponsor] Axis Prize"',
      'Right of first refusal on every Grand Finale finalist',
      'Logo on all 6 prize categories + every comms touchpoint',
      'Title slot at the Grand Finale 2-day spectacle',
      'Annual seat on the Methodology Review Board',
    ],
  },
  {
    tier: '🥇 Category Sovereign Partner',
    name: 'Category',
    price: 'USD $25 – 60K',
    items: [
      'Name on one specific prize category',
      'Jury seat for that category',
      'Pro-rata investment rights on the category winner',
      'Full keynote slot at the Grand Finale',
      'Logo on the category prize trophy + certificate',
    ],
  },
  {
    tier: '🎤 Stage Partner',
    name: 'Stage',
    price: 'USD $15K',
    items: [
      'Logo on the venue\'s main stage backdrop',
      '30-second branded reel between every segment',
      'Founder Dinner seat (closed networking, top 16 finalists)',
      'Dedicated thank-you in the press kit',
    ],
  },
  {
    tier: '📺 Media Partner',
    name: 'Media',
    price: 'Barter',
    items: [
      'Press, livestream, podcast, or print syndication rights',
      'Co-branded coverage during Salon + Grand Finale',
      'Exclusive backstage interviews with finalists',
      'Annual Report co-distribution rights',
    ],
  },
];

const ROADMAP = [
  { yr: '2026', name: 'Cohort 1 Demo Day', d: 'November 2026 · Internal pilot · Investors Circle private · 6 Cohort 1 founders · no public broadcast · Hall of Fame induction.' },
  { yr: 'Q1 2027', name: 'First Salon', d: 'First public Salon · pairs with Cohort 2 · LinkedIn Live · USD $25K pool + first committed term sheet.' },
  { yr: 'Q2 2027', name: 'Second Salon', d: 'Different academic partner · different fund partner · the same format compounds.' },
  { yr: 'Q3 2027', name: 'Third Salon', d: 'Index weight rises to 35%. Methodology Review Board first audit of the framework.' },
  { yr: 'Q4 2027', name: '🏆 Grand Finale · Dubai', d: '2-day flagship paired with the Invest UAE Forum 2027. USD $100K+ pool. 8 finalists from 11 chapters. The biggest stage in MENA early-stage governance.' },
];

const ENGAGE = [
  { intent: 'sponsor', icn: <Crown className="h-4 w-4" />, h: 'Become a Sponsor', d: 'Title, Category, Stage, or Media. From USD $15K barter to $250K+ Sovereign Title naming rights.' },
  { intent: 'partner', icn: <Handshake className="h-4 w-4" />, h: 'Co-host as Partner', d: 'Universities, funds, or industry. One academic anchor per year, one fund anchor per Salon. Shared brand, shared revenue.' },
  { intent: 'jury', icn: <Gavel className="h-4 w-4" />, h: 'Nominate for Jury', d: 'SKI Axis Faculty + investor + sector expert + governance practitioner + alumni co-chairs.' },
  { intent: 'founder', icn: <GraduationCap className="h-4 w-4" />, h: 'Founder Application', d: 'Cohort 1 founders auto-eligible. Cohort 2 onwards: open via Chapter Lead endorsement + Silver Index minimum.' },
];

// =================== INQUIRY MODAL ===================
const InquiryModal = ({ intent, onClose }) => {
  const [form, setForm] = useState({
    full_name: '', email: '', organisation: '', role: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.full_name.trim() || !form.email.trim()) {
      setError('Please share your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/axis-circuit/inquiry`, { ...form, intent });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not submit · please try again or email hello@skiaxis.com.');
    } finally {
      setSubmitting(false);
    }
  };

  const titleMap = {
    sponsor: { eb: 'Sponsor inquiry · Title / Category / Stage / Media', h: 'Become a <em>Sponsor</em>.' },
    partner: { eb: 'Co-host inquiry · University / Fund / Industry', h: 'Partner with <em>SKI Axis</em>.' },
    jury: { eb: 'Jury nomination · Methodology Committee', h: 'Take a <em>jury seat</em>.' },
    founder: { eb: 'Founder application · Cohort 2 onwards', h: 'Compete for <em>The Axis Prize</em>.' },
  };
  const t = titleMap[intent] || { eb: 'Axis Circuit', h: 'Open a <em>conversation</em>.' };

  if (done) {
    return (
      <div className="cir-modal-bg" onClick={onClose} data-testid="cir-modal-bg">
        <div className="cir-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cir-success" data-testid="cir-modal-success">
            <div className="cir-success-icn"><CheckCircle2 className="h-7 w-7" /></div>
            <h2 className="cir-success-h">We're <em>on it</em>.</h2>
            <p className="cir-success-p">
              Thank you, {form.full_name.split(' ')[0]}. Your inquiry has been routed to the Axis Circuit team. {intent === 'sponsor' || intent === 'partner' ? "Expect a personal call within 48 hours." : intent === 'jury' ? "Expect vetting committee outreach within 72 hours." : "Cohort 2 applications open Q1 2027 — we'll be in touch with the application window and Silver Index requirement."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cir-modal-bg" onClick={onClose} data-testid="cir-modal-bg">
      <div className="cir-modal" onClick={(e) => e.stopPropagation()} data-testid="cir-modal">
        <div className="cir-modal-hd">
          <div>
            <div className="cir-modal-eb">{t.eb}</div>
            <h2 className="cir-modal-h" dangerouslySetInnerHTML={{ __html: t.h }} />
          </div>
          <button className="cir-modal-x" onClick={onClose} aria-label="Close" data-testid="cir-modal-close">×</button>
        </div>
        <form className="cir-modal-bd" onSubmit={submit} data-testid="cir-modal-form">
          {error && <div className="cir-err" data-testid="cir-modal-error"><AlertCircle className="h-4 w-4" /><span>{error}</span></div>}
          <div className="cir-fld">
            <label className="cir-lbl">Full name <em>*</em></label>
            <input className="cir-input" data-testid="cir-modal-name" value={form.full_name} onChange={(e) => update('full_name', e.target.value)} required />
          </div>
          <div className="cir-fld">
            <label className="cir-lbl">Email <em>*</em></label>
            <input className="cir-input" type="email" data-testid="cir-modal-email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
          </div>
          <div className="cir-fld">
            <label className="cir-lbl">Organisation</label>
            <input className="cir-input" data-testid="cir-modal-org" value={form.organisation} onChange={(e) => update('organisation', e.target.value)} />
          </div>
          <div className="cir-fld">
            <label className="cir-lbl">Your role</label>
            <input className="cir-input" placeholder={intent === 'sponsor' ? 'Marketing / Sponsorship lead' : intent === 'partner' ? 'Faculty / Director / Managing Partner' : intent === 'jury' ? 'Investor / Founder / Academic' : 'Founder / CEO'} data-testid="cir-modal-role" value={form.role} onChange={(e) => update('role', e.target.value)} />
          </div>
          <div className="cir-fld">
            <label className="cir-lbl">Anything else? (optional)</label>
            <textarea className="cir-area" data-testid="cir-modal-notes" maxLength={1500} value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder={intent === 'sponsor' ? 'Tier you are considering, brand objectives, timing…' : intent === 'founder' ? 'Stage, cohort target, jurisdiction, traction…' : ''} />
          </div>
          <button type="submit" className="cir-submit" disabled={submitting} data-testid="cir-modal-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScrollText className="h-4 w-4" />}
            {submitting ? 'Submitting…' : 'Submit inquiry'}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </button>
        </form>
      </div>
    </div>
  );
};

// =================== PAGE ===================
export default function AxisCircuitPage() {
  useSEO({
    title: 'The Axis Circuit · The Global Tournament for Governance-First Founders | SKI Axis',
    description: 'A four-stage tournament for founders graduated through The Axis Index. 11 Chapter Qualifiers · 3 Regional Salons · 1 Grand Finale. USD $100K+ cash + committed term sheets + Hall of Fame.',
  });
  const [modalIntent, setModalIntent] = useState(null);

  return (
    <div className="cir-root" data-testid="axis-circuit-page">
      <style>{STYLES}</style>
      <Header />

      <div className="cir-shell">
        {/* HERO */}
        <div className="cir-hero">
          <div className="cir-eb">The Axis Circuit · Tournament Architecture · v1</div>
          <h1 className="cir-h1">
            The world's first <em>tournament</em> for governance-first founders.<sup>™</sup>
          </h1>
          <p className="cir-sub">
            Eleven Chapter Qualifiers. Three Regional Salons. One Grand Finale paired with the
            Invest UAE Forum. Cash, committed term sheets, and Hall of Fame induction — anchored by
            The Axis Index™ as 30% of every score.
          </p>
          <div className="cir-meta-row">
            <span><em>Cadence</em> · Quarterly per Cohort</span>
            <span><em>Eligibility</em> · Silver Index minimum</span>
            <span><em>First Demo Day</em> · November 2026</span>
            <span><em>Grand Finale</em> · Q4 2027</span>
          </div>
          <div className="cir-cta-row">
            <button className="cir-cta" onClick={() => setModalIntent('sponsor')} data-testid="cir-hero-sponsor">
              Become a sponsor <ArrowRight className="h-4 w-4" />
            </button>
            <button className="cir-cta-2" onClick={() => setModalIntent('partner')} data-testid="cir-hero-partner">
              <Handshake className="h-4 w-4" /> Partner inquiry
            </button>
          </div>
        </div>

        {/* MANIFESTO */}
        <div className="cir-manifesto" data-testid="cir-manifesto">
          <div className="cir-manifesto-eb">Why This Exists</div>
          <h2 className="cir-manifesto-h">A demo day is one stage. <em>This is a year.</em></h2>
          <p className="cir-manifesto-p">
            Existing competitions — IIT Bombay's Eureka, Endeavor's Scaleup Awards, Y Combinator Demo Day — are <strong>events</strong>.
            They run once. They reward pitch performance. They mostly hand out cash.
          </p>
          <p className="cir-manifesto-p">
            The Axis Circuit is a <strong>tournament</strong>. It runs year-round across 11 Chapter cities. It rewards governance maturity
            measured against The Axis Index v1.2 — not just the best ten minutes on stage. And the prize that matters most is not cash —
            it is a <strong>committed term sheet</strong> from a Title Sovereign Partner.
          </p>
          <p className="cir-manifesto-p">
            Eureka can't replicate this. They have one stage, one weekend, one cohort. We have eleven local leagues, three regional
            semifinals, and one global stage paired with the Invest UAE Forum every year. The road to the Axis Prize is itself the story.
          </p>
        </div>

        {/* TOURNAMENT FORMAT */}
        <div className="cir-format" data-testid="cir-format">
          <h2 className="cir-h2">The <em>format</em>.</h2>
          <p className="cir-h2-sub">Three stages. Twelve months. One trophy. Each Chapter Lead scouts; each region semifinal; one final stage in Dubai.</p>
          <div className="cir-format-rail">
            {STAGES.map((s, i) => (
              <div key={i} className="cir-stage" data-testid={`cir-stage-${i + 1}`}>
                <div className="cir-stage-num">{s.num}</div>
                <h3 className="cir-stage-h" dangerouslySetInnerHTML={{ __html: s.h }} />
                <span className="cir-stage-when">{s.when}</span>
                <p className="cir-stage-d">{s.d}</p>
                {s.cities && (
                  <div className="cir-cities">{s.cities.map((c) => <span key={c} className="cir-city">{c}</span>)}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* PRIZES */}
        <div className="cir-prizes" data-testid="cir-prizes">
          <h2 className="cir-h2">Six <em>prize categories</em>.</h2>
          <p className="cir-h2-sub">Every finalist wins something. Cash plus a committed term sheet plus a permanent place in the Hall of Recognition.</p>
          <div className="cir-prize-grid">
            {PRIZES.map((p, i) => (
              <div key={i} className={`cir-prize ${p.feat ? 'feat' : ''}`} data-testid={`cir-prize-${i}`}>
                <div className="cir-prize-icn">{p.icn}</div>
                <div className="cir-prize-eb">{p.eb}</div>
                <h3 className="cir-prize-name">{p.name}</h3>
                <div className="cir-prize-amt">{p.amt}</div>
                <p className="cir-prize-d">{p.d}</p>
                <div className="cir-prize-extra">{p.extra}</div>
              </div>
            ))}
          </div>
        </div>

        {/* SPONSOR TIERS */}
        <div className="cir-sponsors" data-testid="cir-sponsors">
          <h2 className="cir-h2">Four <em>sponsor tiers</em>.</h2>
          <p className="cir-h2-sub">Title naming rights, category-anchored sponsorship, stage-level activation, or a barter-based media partnership. Three Category Partners + one Title Sovereign Partner = USD $325K+ revenue per Grand Finale, before ticket sales.</p>
          <div className="cir-spons-grid">
            {SPONSOR_TIERS.map((s) => (
              <div key={s.name} className="cir-spon" data-testid={`cir-sponsor-${s.name.toLowerCase()}`}>
                <div className="cir-spon-tier">{s.tier}</div>
                <h3 className="cir-spon-name">{s.name}</h3>
                <div className="cir-spon-price">{s.price}</div>
                <ul>{s.items.map((it, k) => <li key={k}>{it}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>

        {/* ROADMAP */}
        <div className="cir-roadmap" data-testid="cir-roadmap">
          <h2 className="cir-h2">A <em>year-long</em> arc.</h2>
          <p className="cir-h2-sub">Five public moments across 14 months. Each one a content drop. Each one a sponsor moment. Each one a step toward the Grand Finale.</p>
          <div className="cir-rm-rail">
            {ROADMAP.map((r, i) => (
              <div key={i} className="cir-rm" data-testid={`cir-rm-${i}`}>
                <div className="cir-rm-yr">{r.yr}</div>
                <h3 className="cir-rm-name">{r.name}</h3>
                <p className="cir-rm-d">{r.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* INDEX TIE-IN */}
        <div className="cir-tieIn" data-testid="cir-tieIn">
          <div className="cir-tieIn-l">
            <div className="cir-tieIn-eb">Anchored by The Axis Index™</div>
            <p className="cir-tieIn-d">
              Every Circuit score is 70% live pitch + <strong>30%</strong> Axis Index measured governance maturity.
              The methodology grows in weight every year — 30% (2026) → 40% (2028) → 50% (2030).
            </p>
          </div>
          <Link to="/axis-index" className="cir-cta" data-testid="cir-tieIn-cta">
            Read the methodology <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* FOUR ENGAGEMENT CTAS */}
        <div className="cir-engage" data-testid="cir-engage">
          <div className="cir-engage-eb">Engage with The Axis Circuit</div>
          <h2 className="cir-engage-h">Four <em>ways in</em>.</h2>
          <p className="cir-h2-sub" style={{ marginBottom: 0 }}>Sponsor a tier. Co-host as a partner. Sit on the jury. Compete as a founder. Each conversation routes to a different team — pick your lane.</p>
          <div className="cir-engage-grid">
            {ENGAGE.map((e) => (
              <button key={e.intent} className="cir-engage-card" onClick={() => setModalIntent(e.intent)} data-testid={`cir-engage-${e.intent}`}>
                <div className="cir-engage-card-icn">{e.icn}</div>
                <h3 className="cir-engage-card-h">{e.h}</h3>
                <p className="cir-engage-card-d">{e.d}</p>
                <div className="cir-engage-card-cta">Open inquiry <ArrowRight className="h-3 w-3" /></div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <Footer />

      {modalIntent && <InquiryModal intent={modalIntent} onClose={() => setModalIntent(null)} />}
    </div>
  );
}
